// src/components/ClientsJobs.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiRequest } from "../api";

import HeadquartersPage from "./headquarters/HeadquartersPage";
import HeadquartersSection from "./headquarters/HeadquartersSection";
import HeadquartersGrid from "./headquarters/HeadquartersGrid";
import HeadquartersMetricCard from "./headquarters/HeadquartersMetricCard";
import MissionCard from "./customer_success/MissionCard";
import { getFirstCustomerSuccessMission } from "./customer_success/customerSuccessEngine";

export default function ClientsJobs() {
  const navigate = useNavigate();

  // data
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);

  // ui state
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [showCommandCenter, setShowCommandCenter] = useState(false);
  const [dismissedMission, setDismissedMission] = useState("");

  function dismissMission(key) {
    setDismissedMission(key);
  }

  function reopenMission() {
    setDismissedMission("");
  }

  function MissionHelpButton({ label = "Help" }) {
    return (
      <button
        type="button"
        onClick={reopenMission}
        style={{
          marginBottom: 18,
          padding: 0,
          border: "none",
          background: "transparent",
          color: "#00c2ff",
          fontWeight: 800,
          cursor: "pointer",
          textDecoration: "underline",
          textUnderlineOffset: 3,
          fontSize: 14,
        }}
      >
        {label}
      </button>
    );
  }

  // forms — client
  const [newClient, setNewClient] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    notes: "",
  });

  // forms — job
  const [newJob, setNewJob] = useState({
    client_id: "",
    service_address: "",
    defendant_name: "",
    case_number: "",
    court_name: "",
    documents: "",
    special_instructions: "",
  });

  // ---- API helpers (cookie-based, via /api) ----
  async function loadClients() {
    return apiRequest("/clients");
  }

  async function loadJobs() {
    return apiRequest("/jobs/");
  }

  async function initialLoad() {
    setLoading(true);
    setErr("");
    try {
      const [c, j] = await Promise.all([loadClients(), loadJobs()]);
      setClients(Array.isArray(c) ? c : []);
      setJobs(Array.isArray(j) ? j : []);
    } catch (e) {
      const msg = (e && e.message) || "Failed to load data";

      if (String(msg).toLowerCase().includes("unauthorized")) {
        setErr("Unauthorized while loading jobs.");
        return;
      }

      setErr(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    initialLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleAddClient(e) {
    e.preventDefault();
    setErr("");
    try {
      await apiRequest("/clients", { method: "POST", body: newClient });
      setNewClient({
        company_name: "",
        contact_name: "",
        email: "",
        phone: "",
        address: "",
        notes: "",
      });
      const c = await loadClients();
      setClients(Array.isArray(c) ? c : []);
      setShowCreateClient(false);
      setShowCreateJob(false);
      setShowCommandCenter(false);
    } catch (e) {
      const msg = (e && e.message) || "Error creating client";

      if (String(msg).toLowerCase().includes("unauthorized")) {
        setErr("Unauthorized while creating client.");
        return;
      }

      setErr(msg);
    }
  }

  async function handleAddJob(e) {
    e.preventDefault();
    if (!newJob.client_id) {
      setErr("Select a client before creating a job.");
      return;
    }

    setErr("");
    try {
      const payload = { ...newJob, client_id: Number(newJob.client_id) };
      await apiRequest("/jobs/", { method: "POST", body: payload });
      setNewJob({
        client_id: "",
        service_address: "",
        defendant_name: "",
        case_number: "",
        court_name: "",
        documents: "",
        special_instructions: "",
      });
      const j = await loadJobs();
      setJobs(Array.isArray(j) ? j : []);
      setShowCreateClient(false);
      setShowCreateJob(false);
      setShowCommandCenter(false);
    } catch (e) {
      const msg = (e && e.message) || "Error creating job";

      if (String(msg).toLowerCase().includes("unauthorized")) {
        setErr("Unauthorized while creating job.");
        return;
      }

      setErr(msg);
    }
  }

  const clientNameById = useMemo(() => {
    const m = {};
    (clients || []).forEach((c) => {
      m[c.id] = c.company_name || c.contact_name || `Client #${c.id}`;
    });
    return m;
  }, [clients]);

  const clientOptions = useMemo(
    () =>
      (clients || []).map((c) => ({
        id: c.id,
        label: c.company_name || c.contact_name || `Client #${c.id}`,
      })),
    [clients]
  );

  const page = {
    padding: 24,
    maxWidth: 1100,
    margin: "0 auto",
    color: "#ffffff",
  };

  const pageTitle = {
    marginBottom: 8,
    fontSize: 18,
    fontWeight: 700,
    color: "#ffffff",
  };

  const pageSubtitle = {
    color: "#9fb0c3",
    marginTop: 0,
    marginBottom: 18,
    fontSize: 14,
  };

  const card = {
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 16,
    padding: 20,
    background: "#10263a",
    marginBottom: 18,
  };

  const sectionTitle = {
    marginTop: 0,
    marginBottom: 14,
    color: "#ffffff",
    fontSize: 16,
    fontWeight: 700,
  };

  const input = {
    width: "100%",
    padding: 10,
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: 10,
    fontSize: 14,
    background: "#0f2235",
    color: "#ffffff",
    outline: "none",
    boxSizing: "border-box",
    transition: "all 0.2s ease",
  };

  const actionRow = {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 18,
  };


  const label = {
    display: "block",
    marginBottom: 12,
  };

  const labelTitle = {
    fontSize: 13,
    color: "#dbe7f3",
    marginBottom: 6,
    fontWeight: 600,
  };

  const button = {
    padding: "10px 16px",
    borderRadius: 10,
    border: "1px solid #f5c542",
    background: "rgba(245, 197, 66, 0.08)",
    color: "#f5c542",
    boxShadow: "0 0 0 1px rgba(245, 197, 66, 0.08)",
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s ease",
  };

  const actionButton = {
    ...button,
    width: "auto",
    minWidth: 150,
  };


  const th = {
    textAlign: "left",
    padding: "10px 8px",
    borderBottom: "1px solid rgba(255,255,255,0.10)",
    fontWeight: 700,
    fontSize: 13,
    color: "#dbe7f3",
    whiteSpace: "nowrap",
  };

  const td = {
    padding: "10px 8px",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    fontSize: 13,
    verticalAlign: "top",
    color: "#ffffff",
  };

  const linkStyle = {
    color: "#00c2ff",
    textDecoration: "none",
    fontWeight: 600,
  };

  const workflowGreen = {
    color: "#22c55e",
    textDecoration: "none",
    fontWeight: 900,
  };

  const workflowGreenHeader = {
    ...th,
    color: "#22c55e",
    fontWeight: 900,
  };

  function inputFocusProps() {
    return {
      onFocus: (e) => {
        e.target.style.border = "1px solid #f5c542";
        e.target.style.boxShadow = "0 0 0 3px rgba(245,197,66,0.12)";
      },
      onBlur: (e) => {
        e.target.style.border = "1px solid rgba(255,255,255,0.10)";
        e.target.style.boxShadow = "none";
      },
    };
  }

  function buttonHoverProps() {
    return {
      onMouseEnter: (e) => {
        e.currentTarget.style.background = "rgba(245, 197, 66, 0.14)";
        e.currentTarget.style.transform = "translateY(-1px)";
      },
      onMouseLeave: (e) => {
        e.currentTarget.style.background = "rgba(245, 197, 66, 0.08)";
        e.currentTarget.style.transform = "translateY(0)";
      },
    };
  }


  const currentMission = getFirstCustomerSuccessMission({ clients, jobs });

  function handleMissionAction(action) {
    if (action === "create-client") {
      setShowCreateClient(true);
      setShowCreateJob(false);
      setShowCommandCenter(false);
      return;
    }

    if (action === "create-job") {
      setShowCreateJob(true);
      setShowCreateClient(false);
      setShowCommandCenter(false);
    }
  }

  function rowHoverProps() {
    return {
      onMouseEnter: (e) => {
        e.currentTarget.style.background = "#16324a";
      },
      onMouseLeave: (e) => {
        e.currentTarget.style.background = "transparent";
      },
    };
  }

  function getJobStatus(job) {
    return job.status || job.job_status || "Prospect";
  }

  function statusBadgeStyle(status) {
    const normalized = String(status || "").toLowerCase();

    let bg = "rgba(245,197,66,0.14)";
    let color = "#f5c542";
    let border = "rgba(245,197,66,0.35)";

    if (normalized.includes("active")) {
      bg = "rgba(0,194,255,0.12)";
      color = "#00c2ff";
      border = "rgba(0,194,255,0.35)";
    }

    if (normalized.includes("paid") || normalized.includes("closed")) {
      bg = "rgba(52,211,153,0.12)";
      color = "#34d399";
      border = "rgba(52,211,153,0.35)";
    }

    if (normalized.includes("cancel")) {
      bg = "rgba(255,138,138,0.10)";
      color = "#ff8a8a";
      border = "rgba(255,138,138,0.30)";
    }

    return {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "4px 9px",
      borderRadius: 999,
      border: `1px solid ${border}`,
      background: bg,
      color,
      fontSize: 12,
      fontWeight: 800,
      whiteSpace: "nowrap",
    };
  }

  return (
    <HeadquartersPage
      title="Home"
      subtitle="Manage clients, jobs, estimates, invoices, payments, and operational workflow from one system."
    >

      {!loading && currentMission && (
        dismissedMission === currentMission.key ? (
          <MissionHelpButton label={currentMission.helpLabel} />
        ) : (
          <MissionCard
            step={currentMission.step}
            total={currentMission.total}
            title={currentMission.title}
            body={currentMission.body}
            primaryLabel={currentMission.primaryLabel}
            onPrimary={() => handleMissionAction(currentMission.action)}
            onDismiss={() => dismissMission(currentMission.key)}
          />
        )
      )}

      <HeadquartersSection
        title="Take Action"
        subtitle="Start with the client, create the job, then manage the workflow from the job record."
      >
        <div style={actionRow}>
          <button
            type="button"
            style={actionButton}
            onClick={() => {
              setShowCreateClient((v) => !v);
              setShowCreateJob(false);
              setShowCommandCenter(false);
            }}
            {...buttonHoverProps()}
          >
            {showCreateClient ? "Hide Create Client" : "Create Client"}
          </button>

          <button
            type="button"
            style={actionButton}
            onClick={() => {
              setShowCreateJob((v) => !v);
              setShowCreateClient(false);
              setShowCommandCenter(false);
            }}
            {...buttonHoverProps()}
          >
            {showCreateJob ? "Hide Create Job" : "Create Job"}
          </button>

          <button
            type="button"
            style={actionButton}
            onClick={() => {
              setShowCommandCenter((v) => !v);
              setShowCreateClient(false);
              setShowCreateJob(false);
            }}
            {...buttonHoverProps()}
          >
            {showCommandCenter ? "Hide Command Center" : "Command Center"}
          </button>
        </div>
      </HeadquartersSection>

      {showCreateClient && (
        <section style={card}>
          <h3 style={sectionTitle}>Create Client</h3>
          <p style={{ color: "#9fb0c3", marginTop: 0, marginBottom: 16 }}>
            Add the client first, then create the job from this same workflow.
          </p>

          <form onSubmit={handleAddClient}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <label style={label}>
                <div style={labelTitle}>Company Name</div>
                <input
                  type="text"
                  value={newClient.company_name}
                  onChange={(e) =>
                    setNewClient({ ...newClient, company_name: e.target.value })
                  }
                  style={input}
                  {...inputFocusProps()}
                />
              </label>

              <label style={label}>
                <div style={labelTitle}>Contact Name</div>
                <input
                  type="text"
                  value={newClient.contact_name}
                  onChange={(e) =>
                    setNewClient({ ...newClient, contact_name: e.target.value })
                  }
                  style={input}
                  {...inputFocusProps()}
                />
              </label>

              <label style={label}>
                <div style={labelTitle}>Email</div>
                <input
                  type="email"
                  value={newClient.email}
                  onChange={(e) =>
                    setNewClient({ ...newClient, email: e.target.value })
                  }
                  style={input}
                  {...inputFocusProps()}
                />
              </label>

              <label style={label}>
                <div style={labelTitle}>Phone</div>
                <input
                  type="text"
                  value={newClient.phone}
                  onChange={(e) =>
                    setNewClient({ ...newClient, phone: e.target.value })
                  }
                  style={input}
                  {...inputFocusProps()}
                />
              </label>
            </div>

            <label style={label}>
              <div style={labelTitle}>Address</div>
              <input
                type="text"
                value={newClient.address}
                onChange={(e) =>
                  setNewClient({ ...newClient, address: e.target.value })
                }
                style={input}
                {...inputFocusProps()}
              />
            </label>

            <label style={label}>
              <div style={labelTitle}>Notes</div>
              <textarea
                value={newClient.notes}
                onChange={(e) =>
                  setNewClient({ ...newClient, notes: e.target.value })
                }
                rows={3}
                style={{ ...input, resize: "vertical" }}
                {...inputFocusProps()}
              />
            </label>

            <button type="submit" style={button} {...buttonHoverProps()}>
              Create Client
            </button>
          </form>
        </section>
      )}

      {showCommandCenter && (
        <section style={card}>
          <h3 style={sectionTitle}>Operations Snapshot</h3>
          <p style={{ color: "#9fb0c3", marginTop: 0, marginBottom: 16 }}>
            Current operational activity for this workspace. Jobs are the center of the workflow.
          </p>

          <HeadquartersGrid min={180}>
            <HeadquartersMetricCard
              label="Open Jobs"
              value={jobs.length}
              subtext="Jobs currently visible in this workspace"
            />

            <HeadquartersMetricCard
              label="Clients"
              value={clients.length}
              subtext="Client records connected to this workspace"
            />

            <HeadquartersMetricCard
              label="Estimated Revenue"
              value={`$${jobs.reduce((sum, job) => sum + (Number(job.documents) || 0), 0).toFixed(2)}`}
              subtext="Projected revenue entered on job records"
            />
          </HeadquartersGrid>
        </section>
      )}

      {err && (
        <div
          role="alert"
          style={{
            background: "rgba(255,138,138,0.10)",
            border: "1px solid rgba(255,138,138,0.30)",
            color: "#ff8a8a",
            padding: "10px 12px",
            borderRadius: 10,
            marginBottom: 14,
          }}
        >
          {err}
        </div>
      )}

      {showCreateJob && (
        <section style={card}>
          <h3 style={sectionTitle}>Create Job</h3>
          <p style={{ color: "#9fb0c3", marginTop: 0, marginBottom: 16 }}>
            Open a new job under an existing client. Estimates, invoices, payments, and receipts will begin from this job.
          </p>

          <form onSubmit={handleAddJob}>
            <div style={{ color: "#f5c542", fontSize: 13, fontWeight: 800, marginBottom: 10 }}>
              Client & Job Identity
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <label style={label}>
                <div style={labelTitle}>Client</div>
                <select
                  value={newJob.client_id}
                  onChange={(e) =>
                    setNewJob({ ...newJob, client_id: e.target.value })
                  }
                  style={input}
                  {...inputFocusProps()}
                >
                  <option value="">Select Client</option>
                  {clientOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>

              <label style={label}>
                <div style={labelTitle}>Job Name</div>
                <input
                  type="text"
                  value={newJob.defendant_name}
                  onChange={(e) =>
                    setNewJob({ ...newJob, defendant_name: e.target.value })
                  }
                  style={input}
                  {...inputFocusProps()}
                />
              </label>

              <label style={label}>
                <div style={labelTitle}>Job #</div>
                <input
                  type="text"
                  value="Auto-generated on job creation"
                  disabled
                  style={{
                    ...input,
                    opacity: 0.72,
                    cursor: "not-allowed",
                    background: "#0f2235",
                    color: "#9fb0c3",
                  }}
                />
              </label>

              <label style={label}>
                <div style={labelTitle}>Job Type</div>
                <input
                  type="text"
                  placeholder="e.g. Block wall, concrete, landscaping"
                  value={newJob.court_name}
                  onChange={(e) =>
                    setNewJob({ ...newJob, court_name: e.target.value })
                  }
                  style={input}
                  {...inputFocusProps()}
                />
              </label>
            </div>

            <div style={{ color: "#f5c542", fontSize: 13, fontWeight: 800, margin: "6px 0 10px" }}>
              Service & Financial Snapshot
            </div>

            <label style={label}>
              <div style={labelTitle}>Service Address</div>
              <input
                type="text"
                value={newJob.service_address}
                onChange={(e) =>
                  setNewJob({ ...newJob, service_address: e.target.value })
                }
                style={input}
                {...inputFocusProps()}
              />
            </label>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <label style={label}>
                <div style={labelTitle}>Projected Internal Cost ($)</div>
                <input
                  type="number"
                  placeholder="e.g. 3500"
                  value={newJob.documents}
                  onChange={(e) =>
                    setNewJob({ ...newJob, documents: e.target.value })
                  }
                  style={input}
                  {...inputFocusProps()}
                />
              </label>

              <label style={label}>
                <div style={labelTitle}>Actual Internal Cost ($)</div>
                <input
                  type="number"
                  placeholder="e.g. 4200"
                  value={newJob.special_instructions}
                  onChange={(e) =>
                    setNewJob({ ...newJob, special_instructions: e.target.value })
                  }
                  style={input}
                  {...inputFocusProps()}
                />
              </label>
            </div>

            <button type="submit" style={button} {...buttonHoverProps()}>
              Create Job
            </button>
          </form>
        </section>
      )}

      <HeadquartersSection
        title="Jobs List"
        subtitle="The job is the operational center of your business."
      >

        {!loading && jobs.length > 0 && (
          <div
            style={{
              marginBottom: 16,
              padding: 16,
              borderRadius: 14,
              border: "1px solid rgba(0,194,255,0.28)",
              background:
                "linear-gradient(135deg, rgba(0,194,255,0.10), rgba(16,38,58,0.96))",
            }}
          >
            <div
              style={{
                color: "#00c2ff",
                fontSize: 12,
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: 1,
                marginBottom: 6,
              }}
            >
              Customer Success
            </div>
            <div style={{ color: "#c9d8e8", fontSize: 14, lineHeight: 1.55 }}>
              Everything from this point forward happens inside the Job. Click the <span style={workflowGreen}>JOB ID</span> below to open the Job Detail page, then create your first estimate or invoice.
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ color: "#9fb0c3" }}>Loading…</div>
        ) : jobs.length === 0 ? (
          <div style={{ color: "#9fb0c3" }}>No jobs yet.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={workflowGreenHeader}>Job ID</th>
                  <th style={th}>Client Name</th>
                  <th style={th}>Client #</th>
                  <th style={th}>Job Name</th>
                  <th style={th}>Job Type</th>
                  <th style={th}>Job #</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr
                    key={job.id}
                    onClick={() => navigate(`/jobs/${job.id}`)}
                    style={{
                      cursor: "pointer",
                      transition: "background 0.15s ease",
                    }}
                    title="Open job details"
                    {...rowHoverProps()}
                  >
                    <td style={td}>
                      <Link
                        to={`/jobs/${job.id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={workflowGreen}
                      >
                        #{job.id}
                      </Link>
                    </td>

                    <td style={td}>
                      <Link
                        to={`/clients/${job.client_id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={linkStyle}
                      >
                        {clientNameById[job.client_id] ||
                          `Client #${job.client_id}`}
                      </Link>
                    </td>

                    <td style={td}>
                      <Link
                        to={`/clients/${job.client_id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={linkStyle}
                      >
                        {job.client_id}
                      </Link>
                    </td>

                    <td style={td}>{job.defendant_name || "—"}</td>
                    <td style={td}>{job.court_name || "—"}</td>
                    <td style={td}>{job.case_number || "—"}</td>

                    <td style={td}>
                      <span style={statusBadgeStyle(getJobStatus(job))}>
                        {getJobStatus(job)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </HeadquartersSection>
    </HeadquartersPage>
  );
}
